import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminListContent, adminCreateContent, adminUpdateContent, adminListCollection, adminListLanguages } from "@/lib/api";
import { CATEGORIES, itemPath } from "@/lib/categories";
import { Plus, Trash2, ArrowLeft, Save, ExternalLink, Check, Loader2, AlertCircle, Cloud } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import ContentTranslations from "@/components/ContentTranslations";
import InlineTranslate from "@/components/InlineTranslate";
import ImageUploader from "@/components/ImageUploader";
import AudioUploader from "@/components/AudioUploader";
import TaxonomyMultiSelect from "@/components/TaxonomyMultiSelect";
import MappingSuggestions from "@/components/MappingSuggestions";
import { toast } from "sonner";

const EMPTY = {
  slug: "", type: "katha", category: "", featured: false, dual_script: false, status: "published",
  categories: [], deities: [], temples: [],
  seo_title: "", seo_description: "", seo_keywords: "", preview_image: "",
  devanagari_title: "", title: "", hindi_title: "", english_title: "",
  description: "", description_hi: "",
  image_url: "", audio_url: "", read_time: "", author: "Dharmartha Editorial",
  deity: "", tradition: "", purpose: "", occasion: "",
  intro: { en: "", hi: "" }, detail_heading: { en: "", hi: "" }, body: { en: "", hi: "" }, meaning: { en: "", hi: "" },
  context: { en: "", hi: "" }, usage: { en: "", hi: "" },
  sanskrit_text: "", transliteration: "",
  materials: [], preparation: { en: "", hi: "" }, sankalpa: "", steps: [], mantras: [], conclusion: { en: "", hi: "" },
  chapters: [],
  muhurta: { label_hi: "", label_en: "", sthapana_date: "", sthapana_window: "", visarjan_date: "", visarjan_window: "", notes_hi: "", notes_en: "" },
  source: "", scriptural_reference: "", textual_reference: "", regional_tradition: "", editorial_note: "",
  references: [], tags: [], related: [], languages: ["hi", "en"],
};

const inputCls = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

const Field = ({ label, value, onChange, placeholder, area, deva, translate }) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-2">
      <label className="block text-xs uppercase tracking-wider text-ink-3">{label}</label>
      {translate}
    </div>
    {area ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className={`${inputCls} ${deva ? "font-deva" : ""}`} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} ${deva ? "font-deva" : ""}`} />
    )}
  </div>
);

const LangField = ({ label, value, onChange, translate }) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-2">
      <label className="block text-xs uppercase tracking-wider text-ink-3">{label}</label>
      {translate}
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      <RichTextEditor value={value.en} onChange={(html) => onChange({ ...value, en: html })} placeholder="English" />
      <RichTextEditor value={value.hi} onChange={(html) => onChange({ ...value, hi: html })} placeholder="हिन्दी" deva />
    </div>
  </div>
);

const LinesField = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label} <span className="normal-case text-ink-3">(one per line)</span></label>
    <textarea value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n").filter((x) => x.trim() !== ""))} placeholder={placeholder} rows={4} className={inputCls} />
  </div>
);

const Section = ({ title, children }) => (
  <div className="border border-line bg-surface p-6">
    <h3 className="mb-5 font-serif-latin text-xl text-ink">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

export default function AdminContentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editing);
  // Autosave state
  const [autosaveId, setAutosaveId] = useState(null); // id of the draft auto-created for NEW content
  const [autoState, setAutoState] = useState("idle"); // idle | saving | saved | error
  const [lastSaved, setLastSaved] = useState(null);
  const [recovered, setRecovered] = useState(null); // local unsaved-changes snapshot offered for restore

  // Refs so the 30s interval always reads the latest values without re-binding.
  const formRef = useRef(form);
  const busyRef = useRef(false);
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const readyRef = useRef(false);
  const skipDirtyRef = useRef(false);
  const autosaveIdRef = useRef(null);
  const loadedUpdatedAtRef = useRef(null);
  formRef.current = form;
  busyRef.current = busy;
  autosaveIdRef.current = autosaveId;

  const LKEY = `dh_form_draft_${id || "new"}`;
  const clearSnapshot = () => { try { localStorage.removeItem(LKEY); } catch (e) {} };

  // Slug → display-name maps so we can keep the legacy single `category`/`deity`
  // fields (used by public detail pages) in sync with the taxonomy selections.
  const taxRef = useRef({ categories: {}, deities: {} });
  const [trLangs, setTrLangs] = useState([]);
  useEffect(() => {
    adminListCollection("categories").then((r) => { taxRef.current.categories = Object.fromEntries(r.map((x) => [x.slug, x.name || x.slug])); }).catch(() => {});
    adminListCollection("deities").then((r) => { taxRef.current.deities = Object.fromEntries(r.map((x) => [x.slug, x.name || x.slug])); }).catch(() => {});
    adminListLanguages().then((ls) => setTrLangs((ls || []).filter((l) => l.code !== "en" && l.code !== "hi"))).catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) {
      adminListContent().then((items) => {
        const found = items.find((x) => x.id === id);
        if (found) {
          loadedUpdatedAtRef.current = found.updated_at || null;
          setForm({
            ...EMPTY,
            ...found,
            title: found.title || found.english_title || "",
            devanagari_title: found.devanagari_title || found.hindi_title || "",
            hindi_title: found.hindi_title || found.devanagari_title || "",
            dual_script: found.dual_script ?? ["mantra", "stotra", "sukta"].includes(found.type),
          });
        }
        setLoaded(true);
      });
    }
  }, [id, editing]);

  // Mark the form dirty on any edit made after the initial load has settled,
  // and snapshot it to localStorage so a reload/crash never loses work.
  useEffect(() => {
    if (!loaded) return;
    if (!readyRef.current) { readyRef.current = true; return; }
    if (skipDirtyRef.current) { skipDirtyRef.current = false; return; }
    dirtyRef.current = true;
    setAutoState((s) => (s === "saving" ? s : "idle"));
    try { localStorage.setItem(LKEY, JSON.stringify({ form, ts: Date.now(), savedId: id || autosaveIdRef.current })); } catch (e) {}
  }, [form, loaded]);

  // On open, offer to restore any newer local snapshot (from a prior reload/crash).
  useEffect(() => {
    if (!loaded) return;
    try {
      const snap = JSON.parse(localStorage.getItem(LKEY) || "null");
      const f = snap?.form;
      if (!f) return;
      const hasContent = f.title || f.devanagari_title || f.slug
        || (f.intro && (f.intro.en || f.intro.hi)) || (f.body && (f.body.en || f.body.hi));
      if (!hasContent) return;
      if (editing) {
        const srv = loadedUpdatedAtRef.current ? Date.parse(loadedUpdatedAtRef.current) : 0;
        if (!(snap.ts > srv + 1000)) return; // server copy is same or newer — nothing to recover
      }
      setRecovered(snap);
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const restoreDraft = () => {
    if (!recovered?.form) return;
    setForm({ ...EMPTY, ...recovered.form });
    if (recovered.savedId && !id) { setAutosaveId(recovered.savedId); autosaveIdRef.current = recovered.savedId; }
    setRecovered(null);
    toast.success("Unsaved changes restored");
  };
  const discardDraft = () => { clearSnapshot(); setRecovered(null); };

  // NOTE: automatic 30s server autosave is intentionally DISABLED while we
  // investigate reports of edited content losing chapters. The non-destructive
  // localStorage snapshot + recovery banner remain active; saving to the server
  // now happens only when the user explicitly clicks Save as Draft / Publish.
  const AUTOSAVE_TO_SERVER = false;

  const autosave = async () => {
    if (!AUTOSAVE_TO_SERVER) return;
    if (!dirtyRef.current || busyRef.current || savingRef.current) return;
    const f = formRef.current;
    // Require some real content so we never persist an empty shell.
    if (!(f.title || f.devanagari_title || f.slug)) return;
    savingRef.current = true;
    setAutoState("saving");
    try {
      const payload = { ...f };
      delete payload.i18n;
      const eid = id || autosaveIdRef.current;
      if (eid) {
        // Existing item — save progress preserving its current status (never unpublish a live page).
        await adminUpdateContent(eid, payload);
      } else {
        // New item — create a hidden draft once, then keep updating it.
        payload.status = "draft";
        const created = await adminCreateContent(payload);
        autosaveIdRef.current = created.id;
        setAutosaveId(created.id);
        skipDirtyRef.current = true;
        setForm((prev) => ({ ...prev, slug: created.slug || prev.slug, status: prev.status === "published" ? prev.status : "draft" }));
      }
      dirtyRef.current = false;
      setLastSaved(new Date());
      setAutoState("saved");
    } catch (e) {
      setAutoState("error");
    } finally {
      savingRef.current = false;
    }
  };

  // Server autosave interval (no-op while AUTOSAVE_TO_SERVER is false).
  useEffect(() => {
    if (!AUTOSAVE_TO_SERVER) return;
    const iv = setInterval(() => { autosave(); }, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // repeatable list helpers
  const addRow = (key, row) => set(key, [...form[key], row]);
  const updateRow = (key, i, row) => set(key, form[key].map((r, idx) => (idx === i ? row : r)));
  const removeRow = (key, i) => set(key, form[key].filter((_, idx) => idx !== i));

  const save = async (statusOverride) => {
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.i18n;
      if (statusOverride) payload.status = statusOverride;
      // Keep legacy display fields in sync with the taxonomy selections.
      const catMap = taxRef.current.categories, deityMap = taxRef.current.deities;
      if ((form.categories || []).length) payload.category = catMap[form.categories[0]] || form.categories[0];
      if ((form.deities || []).length) payload.deity = deityMap[form.deities[0]] || form.deities[0];
      const eid = id || autosaveIdRef.current;
      if (eid) {
        await adminUpdateContent(eid, payload);
        toast(payload.status === "draft" ? "Saved as draft" : "Content updated", { description: payload.title });
      } else {
        await adminCreateContent(payload);
        toast(payload.status === "draft" ? "Draft created" : "Content created", { description: payload.title });
      }
      dirtyRef.current = false;
      clearSnapshot();
      navigate("/admin/content");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Save failed", { description: typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : err.message) });
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <p className="py-16 text-center text-ink-3">…</p>;

  const isPuja = form.type === "puja_vidhi";
  const isKatha = form.type === "katha";
  const isScripture = form.type === "grantha";
  const isMantra = ["mantra", "stotra", "sukta"].includes(form.type);
  const isFestival = form.type === "festival";
  const typeLabel = (CATEGORIES.find((c) => c.type === form.type) || {}).en || form.type;

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => navigate("/admin/content")} className="mb-6 inline-flex items-center gap-2 text-sm text-ink-2 hover:text-saffron">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif-latin text-3xl text-ink">{editing ? "Edit Content" : "New Content"}</h1>
        <div className="flex items-center gap-2">
          <span data-testid="content-status-badge" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${form.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${form.status === "draft" ? "bg-amber-500" : "bg-emerald-500"}`} />
            {form.status === "draft" ? "Draft (hidden from site)" : "Published (live)"}
          </span>
          {editing && form.slug && (
          <a href={itemPath(form)} target="_blank" rel="noopener noreferrer" data-testid="form-view-live"
            className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
            <ExternalLink className="h-3.5 w-3.5" /> View on site
          </a>
          )}
        </div>
      </div>
      <p className="mb-8 text-sm text-ink-3">Showing the fields relevant to <span className="text-saffron">{typeLabel}</span>. Change the Type to adapt the form. Prose fields accept both English and हिन्दी; Sanskrit text is entered in Devanagari.</p>

      {recovered && (
        <div data-testid="recover-banner" className="mb-6 flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 px-4 py-3">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
          <p className="flex-1 text-sm text-amber-900">
            We found <span className="font-medium">unsaved changes</span> from {new Date(recovered.ts).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} (this page may have reloaded). Restore them?
          </p>
          <button data-testid="recover-restore" onClick={restoreDraft} className="inline-flex items-center gap-1.5 bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700">
            Restore changes
          </button>
          <button data-testid="recover-discard" onClick={discardDraft} className="inline-flex items-center gap-1.5 border border-amber-300 px-3 py-1.5 text-xs text-amber-800 transition-colors hover:bg-amber-100">
            Discard
          </button>
        </div>
      )}

      <div className="space-y-6">
        <Section title="Basics">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Type</label>
              <select value={form.type} onChange={(e) => { const tp = e.target.value; setForm((f) => ({ ...f, type: tp, dual_script: ["mantra", "stotra", "sukta"].includes(tp) ? true : f.dual_script })); }} className={inputCls} data-testid="form-type">
                {CATEGORIES.map((c) => <option key={c.type} value={c.type}>{c.en}</option>)}
              </select>
            </div>
            <TaxonomyMultiSelect label="Categories" name="categories" value={form.categories} onChange={(v) => set("categories", v)} testid="tax-categories" hint="Group content (e.g. Home Pooja, Navratri). Manage the list under Taxonomy." />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title (English / Latin)" value={form.title} onChange={(v) => set("title", v)} placeholder="e.g. Mahamrityunjaya Mantra" translate={<InlineTranslate itemId={editing ? id : null} field="title" text={form.title} languages={trLangs} />} />
            <Field label="Title (हिन्दी / Devanagari)" value={form.devanagari_title} onChange={(v) => { set("devanagari_title", v); set("hindi_title", v); }} placeholder="e.g. महामृत्युंजय मंत्र" deva />
          </div>
          <label className="flex items-start gap-2.5 rounded-none border border-line bg-parchment/50 p-3.5">
            <input type="checkbox" checked={form.dual_script} onChange={(e) => set("dual_script", e.target.checked)} data-testid="form-dualscript" className="mt-0.5 h-4 w-4 accent-[var(--saffron)]" />
            <span className="text-sm text-ink-2">Show <span className="font-medium text-ink">both scripts together</span> (Devanagari + Latin transliteration) on the site. Recommended for <span className="text-saffron">mantras, stotras &amp; sacred verses</span>; leave off for kathas, festivals &amp; articles.</span>
          </label>
          <Field label="Slug (optional — auto-generated)" value={form.slug} onChange={(v) => set("slug", v)} placeholder="mahamrityunjaya-mantra" />
          <Field label="Short Description (English)" value={form.description} onChange={(v) => set("description", v)} area translate={<InlineTranslate itemId={editing ? id : null} field="description" text={form.description} languages={trLangs} />} />
          <Field label="Short Description (Hindi)" value={form.description_hi} onChange={(v) => set("description_hi", v)} area />
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Read time" value={form.read_time} onChange={(v) => set("read_time", v)} placeholder="4 min" />
            <Field label="Author / Editor" value={form.author} onChange={(v) => set("author", v)} />
            <label className="flex items-end gap-2 pb-2">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" className="h-4 w-4 accent-[var(--saffron)]" />
              <span className="text-sm text-ink-2">Featured</span>
            </label>
          </div>
          <ImageUploader label="Cover image" value={form.image_url} onChange={(v) => set("image_url", v)} testid="content-image" />
          <ImageUploader label="Preview / share image (optional)" value={form.preview_image} onChange={(v) => set("preview_image", v)} testid="content-preview-image" hint="Shown when this page is shared on WhatsApp, Facebook, etc. Leave blank to use the cover image above." />
          <div className="grid gap-4 sm:grid-cols-2">
            <TaxonomyMultiSelect label="Deities" name="deities" value={form.deities} onChange={(v) => set("deities", v)} testid="tax-deities" hint="Map to one or more deities for the site's deity pages & filters." />
            <TaxonomyMultiSelect label="Temples" name="temples" value={form.temples} onChange={(v) => set("temples", v)} testid="tax-temples" hint="Link this content to related temples." />
          </div>
          <MappingSuggestions
            type={form.type} slug={editing ? form.slug : ""}
            deities={form.deities} temples={form.temples} categories={form.categories}
            related={form.related} tags={form.tags}
            onAdd={(field, slugs) => setForm((f) => ({ ...f, [field]: Array.from(new Set([...(f[field] || []), ...slugs])) }))}
          />
          <div className={`grid gap-4 ${isFestival || isPuja ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            <Field label="Tradition" value={form.tradition} onChange={(v) => set("tradition", v)} />
            <Field label="Purpose" value={form.purpose} onChange={(v) => set("purpose", v)} />
            {(isFestival || isPuja) && <Field label="Occasion" value={form.occasion} onChange={(v) => set("occasion", v)} />}
          </div>
        </Section>

        <Section title="Introduction & Content">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Intro heading (English)" value={form.detail_heading?.en || ""} onChange={(v) => set("detail_heading", { ...(form.detail_heading || {}), en: v })} placeholder="e.g. About this text" />
            <Field label="Intro heading (हिन्दी)" value={form.detail_heading?.hi || ""} onChange={(v) => set("detail_heading", { ...(form.detail_heading || {}), hi: v })} placeholder="इस पाठ के बारे में" deva />
          </div>
          <LangField label="Intro (shown at the top of the page)" value={form.intro} onChange={(v) => set("intro", v)} translate={<InlineTranslate itemId={editing ? id : null} field="intro" text={form.intro?.en || ""} languages={trLangs} />} />
          {(isKatha || isFestival) && <LangField label="Overview / Body" value={form.body} onChange={(v) => set("body", v)} translate={<InlineTranslate itemId={editing ? id : null} field="body" text={form.body?.en || ""} languages={trLangs} />} />}
          {isMantra && <LangField label="Meaning" value={form.meaning} onChange={(v) => set("meaning", v)} translate={<InlineTranslate itemId={editing ? id : null} field="meaning" text={form.meaning?.en || ""} languages={trLangs} />} />}
          {(isMantra || isFestival) && <LangField label={isFestival ? "Significance / Context" : "Context"} value={form.context} onChange={(v) => set("context", v)} translate={<InlineTranslate itemId={editing ? id : null} field="context" text={form.context?.en || ""} languages={trLangs} />} />}
          {isMantra && <LangField label="How it is used" value={form.usage} onChange={(v) => set("usage", v)} translate={<InlineTranslate itemId={editing ? id : null} field="usage" text={form.usage?.en || ""} languages={trLangs} />} />}
        </Section>

        {!isMantra && (
          <Section title="Audio recitation (optional)">
            <AudioUploader label="Audio recitation / narration" value={form.audio_url} onChange={(v) => set("audio_url", v)} testid="content-audio" />
          </Section>
        )}

        {isMantra && (
          <Section title="Sacred Text (Devanagari)">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">Sanskrit / Devanagari text</label>
              <RichTextEditor value={form.sanskrit_text} onChange={(html) => set("sanskrit_text", html)} placeholder="मूल पाठ — verses; use ॥ १ ॥ markers or the formatting toolbar" deva />
              <p className="mt-1 text-xs text-ink-3">Tip: keep <span className="font-deva">॥ १ ॥</span> verse markers for the paired verse layout & Focus Reader. Rich formatting renders as-is.</p>
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-ink">Transliteration (IAST)</label>
              <RichTextEditor value={form.transliteration} onChange={(html) => set("transliteration", html)} placeholder="Transliteration (IAST)" />
            </div>
            <div className="mt-4"><AudioUploader label="Audio recitation (optional)" value={form.audio_url} onChange={(v) => set("audio_url", v)} testid="content-audio" /></div>
          </Section>
        )}

        <Section title="SEO (search engines) — optional">
          <p className="-mt-1 text-xs text-ink-3">Leave blank to auto-use the title/description above. Keywords help this page rank for the terms people search.</p>
          <Field label="SEO title" value={form.seo_title} onChange={(v) => set("seo_title", v)} placeholder="Defaults to the title above" />
          <Field label="SEO meta description" value={form.seo_description} onChange={(v) => set("seo_description", v)} placeholder="Defaults to the description above" area />
          <Field label="SEO keywords (comma-separated)" value={form.seo_keywords} onChange={(v) => set("seo_keywords", v)} placeholder="e.g. ganesh aarti, jai ganesh deva, ganpati stotra" />
        </Section>

        {isPuja && (
          <Section title="Pooja Vidhi">
            <LinesField label="Materials (सामग्री)" value={form.materials} onChange={(v) => set("materials", v)} />
            <LangField label="Preparation (पूर्व तैयारी)" value={form.preparation} onChange={(v) => set("preparation", v)} translate={<InlineTranslate itemId={editing ? id : null} field="preparation" text={form.preparation?.en || ""} languages={trLangs} />} />
            <Field label="Sankalpa (संकल्प)" value={form.sankalpa} onChange={(v) => set("sankalpa", v)} area translate={<InlineTranslate itemId={editing ? id : null} field="sankalpa" text={form.sankalpa} languages={trLangs} />} />
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs uppercase tracking-wider text-ink-3">Steps (पूजा विधि)</label>
                <button onClick={() => addRow("steps", { title: "", text: "" })} className="inline-flex items-center gap-1 text-xs text-saffron"><Plus className="h-3 w-3" /> Add step</button>
              </div>
              <div className="space-y-3">
                {form.steps.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="flex-1 space-y-2">
                      <input value={s.title} onChange={(e) => updateRow("steps", i, { ...s, title: e.target.value })} placeholder="Step title" className={inputCls} />
                      <textarea value={s.text} onChange={(e) => updateRow("steps", i, { ...s, text: e.target.value })} placeholder="Step description" rows={2} className={inputCls} />
                    </div>
                    <button onClick={() => removeRow("steps", i)} className="self-start p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs uppercase tracking-wider text-ink-3">Mantras (मंत्र)</label>
                <button onClick={() => addRow("mantras", { devanagari: "", transliteration: "", meaning: "" })} className="inline-flex items-center gap-1 text-xs text-saffron"><Plus className="h-3 w-3" /> Add mantra</button>
              </div>
              <div className="space-y-3">
                {form.mantras.map((m, i) => (
                  <div key={i} className="flex gap-2">
                    <div className="flex-1 space-y-2">
                      <input value={m.devanagari} onChange={(e) => updateRow("mantras", i, { ...m, devanagari: e.target.value })} placeholder="Devanagari" className={`${inputCls} font-deva-body`} />
                      <input value={m.transliteration} onChange={(e) => updateRow("mantras", i, { ...m, transliteration: e.target.value })} placeholder="Transliteration" className={inputCls} />
                      <input value={m.meaning} onChange={(e) => updateRow("mantras", i, { ...m, meaning: e.target.value })} placeholder="Meaning" className={inputCls} />
                    </div>
                    <button onClick={() => removeRow("mantras", i)} className="self-start p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <LangField label="Conclusion (समापन)" value={form.conclusion} onChange={(v) => set("conclusion", v)} translate={<InlineTranslate itemId={editing ? id : null} field="conclusion" text={form.conclusion?.en || ""} languages={trLangs} />} />
          </Section>
        )}

        {(isKatha || isScripture) && (
          <Section title={isScripture ? "Chapters (अध्याय) — verses, meaning, context, benefits & purpose" : "Chapters"}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs text-ink-3">{isScripture ? "Add each chapter/adhyaya with its Sanskrit verses and explanation" : "Add narrative chapters"}</span>
              <button onClick={() => addRow("chapters", { title: "", devanagari_title: "", body_en: "", body_hi: "", sanskrit_text: "", transliteration: "", meaning_en: "", meaning_hi: "", context_en: "", context_hi: "", benefits_en: "", benefits_hi: "", purpose_en: "", purpose_hi: "" })} className="inline-flex items-center gap-1 text-xs text-saffron"><Plus className="h-3 w-3" /> Add chapter</button>
            </div>
            <div className="space-y-4">
              {form.chapters.map((ch, i) => (
                <div key={i} className="border border-line p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-ink-3">Chapter {i + 1}</span>
                    <button onClick={() => removeRow("chapters", i)} className="p-1 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <input value={ch.devanagari_title} onChange={(e) => updateRow("chapters", i, { ...ch, devanagari_title: e.target.value })} placeholder="Devanagari title" className={`${inputCls} font-deva-body`} />
                    <input value={ch.title} onChange={(e) => updateRow("chapters", i, { ...ch, title: e.target.value })} placeholder="English title" className={inputCls} />
                  </div>
                  {isScripture && (
                    <>
                      <textarea value={ch.sanskrit_text || ""} onChange={(e) => updateRow("chapters", i, { ...ch, sanskrit_text: e.target.value })} placeholder="Sanskrit verses (Devanagari) — use ॥ १ ॥ verse markers" rows={4} className={`${inputCls} mt-2 font-deva-body`} />
                      <textarea value={ch.transliteration || ""} onChange={(e) => updateRow("chapters", i, { ...ch, transliteration: e.target.value })} placeholder="Transliteration (IAST)" rows={3} className={`${inputCls} mt-2`} />
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <textarea value={ch.meaning_en || ""} onChange={(e) => updateRow("chapters", i, { ...ch, meaning_en: e.target.value })} placeholder="Meaning (English)" rows={3} className={inputCls} />
                        <textarea value={ch.meaning_hi || ""} onChange={(e) => updateRow("chapters", i, { ...ch, meaning_hi: e.target.value })} placeholder="अर्थ (हिन्दी)" rows={3} className={`${inputCls} font-deva-body`} />
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <textarea value={ch.context_en || ""} onChange={(e) => updateRow("chapters", i, { ...ch, context_en: e.target.value })} placeholder="Context (English)" rows={2} className={inputCls} />
                        <textarea value={ch.context_hi || ""} onChange={(e) => updateRow("chapters", i, { ...ch, context_hi: e.target.value })} placeholder="संदर्भ (हिन्दी)" rows={2} className={`${inputCls} font-deva-body`} />
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <textarea value={ch.benefits_en || ""} onChange={(e) => updateRow("chapters", i, { ...ch, benefits_en: e.target.value })} placeholder="Benefits (English)" rows={2} className={inputCls} />
                        <textarea value={ch.benefits_hi || ""} onChange={(e) => updateRow("chapters", i, { ...ch, benefits_hi: e.target.value })} placeholder="लाभ (हिन्दी)" rows={2} className={`${inputCls} font-deva-body`} />
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <textarea value={ch.purpose_en || ""} onChange={(e) => updateRow("chapters", i, { ...ch, purpose_en: e.target.value })} placeholder="Purpose (English)" rows={2} className={inputCls} />
                        <textarea value={ch.purpose_hi || ""} onChange={(e) => updateRow("chapters", i, { ...ch, purpose_hi: e.target.value })} placeholder="प्रयोजन (हिन्दी)" rows={2} className={`${inputCls} font-deva-body`} />
                      </div>
                    </>
                  )}
                  {!isScripture && (
                    <>
                      <textarea value={ch.body_en} onChange={(e) => updateRow("chapters", i, { ...ch, body_en: e.target.value })} placeholder="Body (English)" rows={3} className={`${inputCls} mt-2`} />
                      <textarea value={ch.body_hi} onChange={(e) => updateRow("chapters", i, { ...ch, body_hi: e.target.value })} placeholder="Body (हिन्दी)" rows={3} className={`${inputCls} mt-2 font-deva-body`} />
                    </>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {(isPuja || isFestival) && (
          <Section title="Muhurta (optional)">
            <p className="-mt-1 text-xs text-ink-3">Fill this in when the puja is tied to a specific festival date — a highlighted muhurta banner will appear at the top of the puja vidhi page.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Label (Hindi)" value={form.muhurta?.label_hi || ""} onChange={(v) => set("muhurta", { ...form.muhurta, label_hi: v })} placeholder="गणेश चतुर्थी २०२६" />
              <Field label="Label (English)" value={form.muhurta?.label_en || ""} onChange={(v) => set("muhurta", { ...form.muhurta, label_en: v })} placeholder="Ganesh Chaturthi 2026" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Sthapana Date" value={form.muhurta?.sthapana_date || ""} onChange={(v) => set("muhurta", { ...form.muhurta, sthapana_date: v })} placeholder="Friday, 11 September 2026" />
              <Field label="Sthapana Window" value={form.muhurta?.sthapana_window || ""} onChange={(v) => set("muhurta", { ...form.muhurta, sthapana_window: v })} placeholder="11:03 AM – 1:34 PM (Madhyahna)" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Visarjan Date" value={form.muhurta?.visarjan_date || ""} onChange={(v) => set("muhurta", { ...form.muhurta, visarjan_date: v })} placeholder="Sunday, 20 September 2026 · Anant Chaturdashi" />
              <Field label="Visarjan Window" value={form.muhurta?.visarjan_window || ""} onChange={(v) => set("muhurta", { ...form.muhurta, visarjan_window: v })} placeholder="Any time on Anant Chaturdashi" />
            </div>
            <Field label="Notes (Hindi)" value={form.muhurta?.notes_hi || ""} onChange={(v) => set("muhurta", { ...form.muhurta, notes_hi: v })} area />
            <Field label="Notes (English)" value={form.muhurta?.notes_en || ""} onChange={(v) => set("muhurta", { ...form.muhurta, notes_en: v })} area />
          </Section>
        )}

        <Section title="Sources & References">
          <Field label="Source / Tradition" value={form.source} onChange={(v) => set("source", v)} placeholder="e.g. Rigveda 10.90; Gita Press" />
          {(isMantra || isPuja) && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Scriptural Reference" value={form.scriptural_reference} onChange={(v) => set("scriptural_reference", v)} />
              <Field label="Textual Reference" value={form.textual_reference} onChange={(v) => set("textual_reference", v)} />
              <Field label="Regional Tradition" value={form.regional_tradition} onChange={(v) => set("regional_tradition", v)} />
            </div>
          )}
          <Field label="Editorial Note" value={form.editorial_note} onChange={(v) => set("editorial_note", v)} area />
          <LinesField label="References" value={form.references} onChange={(v) => set("references", v)} />
          <LinesField label="Tags" value={form.tags} onChange={(v) => set("tags", v)} placeholder="shiva" />
          <LinesField label="Related (slugs)" value={form.related} onChange={(v) => set("related", v)} placeholder="gayatri-mantra" />
        </Section>

        {editing && <ContentTranslations itemId={id} initialI18n={form.i18n} />}

        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 border-t border-line bg-parchment/95 py-4 backdrop-blur">
          <span data-testid="autosave-status" className="mr-auto inline-flex items-center gap-1.5 text-xs text-ink-3">
            {autoState === "saving" && (<><Loader2 className="h-3.5 w-3.5 animate-spin text-saffron" /> Saving draft…</>)}
            {autoState === "saved" && (<><Check className="h-3.5 w-3.5 text-emerald-600" /> Draft saved{lastSaved ? ` · ${lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</>)}
            {autoState === "error" && (<><AlertCircle className="h-3.5 w-3.5 text-amber-600" /> Autosave failed — changes kept locally</>)}
            {autoState === "idle" && (<><Cloud className="h-3.5 w-3.5 text-ink-3" /> Autosaves every 30s</>)}
          </span>
          <button onClick={() => navigate("/admin/content")} className="px-5 py-2.5 text-sm text-ink-2 hover:text-ink">Cancel</button>
          <button data-testid="form-save-draft" onClick={() => save("draft")} disabled={busy} className="inline-flex items-center gap-2 border border-line px-5 py-2.5 text-sm font-medium text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
            {busy ? "Saving…" : "Save as Draft"}
          </button>
          <button data-testid="form-save" onClick={() => save("published")} disabled={busy} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : editing ? "Update & Publish" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
